import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {makeEnumTypeGuard} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useToggle} from '../../../srcCommon/helpers/hookHelpers';
import {AnalyticsReportsEnum} from '../../analytics/helpers/analyticsReportHelpers';
import {buildReportPath} from '../../analytics/helpers/analyticsUrlHelpers';
import {Paragraph} from '../../components/common/paragraph';
import {InlineBanner, InlineBannerTypesEnum} from '../../components/inlineBanner/inlineBanner';
import {BusinessHour, BusinessHourDays} from '../../models/api/businessHoursModel';
import {CalendarWeekDaysShortEnum} from '../../models/api/calendarCommonTypes';
import {InboxModel} from '../../models/api/inboxModel';
import {TeamModel} from '../../models/api/teamModel';

/*
 * Props.
 */

interface SettingsBusinessHoursWarningBannerProps {
  inbox: InboxModel;
  team: TeamModel | undefined;
  navigateToLink: HandlerOf<string>;
}

/*
 * Style.
 */

const StyledWarningInlineBanner = styled(InlineBanner)`
  width: 100%;
  max-width: 600px;
  margin-bottom: 20px;
`;

const StyledTitleBannerTitleParagraph = styled(Paragraph)`
  margin-bottom: 8px;
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledBannerContentDiv = styled.div`
  padding-right: 20px;
  font-size: ${fontSizes.medium};
`;

const StyledButton = styled(Button)`
  display: inline-block;
  margin-top: 8px;
  margin-left: -12px;
`;

const StyledUl = styled.ul`
  padding-left: 20px;
  margin: 0 0 5px 0;
`;

const StyledLi = styled.li`
  font-size: ${fontSizes.medium};
`;

/*
 * Constants.
 */

const mapShortToLongWeekDay = {
  [CalendarWeekDaysShortEnum.MONDAY]: <Trans id="monday-long">Monday</Trans>,
  [CalendarWeekDaysShortEnum.TUESDAY]: <Trans id="tuesday-long">Tuesday</Trans>,
  [CalendarWeekDaysShortEnum.WEDNESDAY]: <Trans id="wednesday-long">Wednesday</Trans>,
  [CalendarWeekDaysShortEnum.THURSDAY]: <Trans id="thursday-long">Thursday</Trans>,
  [CalendarWeekDaysShortEnum.FRIDAY]: <Trans id="friday-long">Friday</Trans>,
  [CalendarWeekDaysShortEnum.SATURDAY]: <Trans id="saturday-long">Saturday</Trans>,
  [CalendarWeekDaysShortEnum.SUNDAY]: <Trans id="sunday-long">Sunday</Trans>,
};

/*
 * Component.
 */

export const SettingsBusinessHoursWarningBanner: FC<SettingsBusinessHoursWarningBannerProps> = ({
  inbox,
  team,
  navigateToLink,
}) => {
  const [displayWarningBanner, toggleDisplayWarningBanner] = useToggle(true);

  if (!team?.preferences.businessHours?.enabled || !displayWarningBanner) {
    return null;
  }

  return (
    <StyledWarningInlineBanner
      type={InlineBannerTypesEnum.WARNING}
      size={VisualSizesEnum.SMALL}
      onClose={toggleDisplayWarningBanner}
      title={
        <>
          <StyledTitleBannerTitleParagraph>
            {' '}
            <Trans id="business-hours-settings-inbox-warning-title">
              Existing team business hours for {team.name}
            </Trans>{' '}
          </StyledTitleBannerTitleParagraph>
          {displayBusinessHoursAsListGroupedByHours(team.preferences.businessHours)}
          <StyledBannerContentDiv>
            <Trans id="business-hours-settings-inbox-warning">
              Business hours set for the {inbox.name} inbox override these team business hours.
            </Trans>
          </StyledBannerContentDiv>
          <StyledButton
            onClick={() => {
              navigateToLink(
                buildReportPath({
                  namespace: team.namespace,
                  reportType: AnalyticsReportsEnum.OVERVIEW,
                }),
              );
            }}
            size={VisualSizesEnum.SMALL}
            variant={ButtonVariantsEnum.LINK}
          >
            <Trans id="business-hours-dialog-inbox-warning-button">
              Edit team business hours in Analytics
            </Trans>
          </StyledButton>
        </>
      }
    />
  );
};

/*
 * Helpers.
 */

/**
 * Nicely format Business Hours by days with identical hours, as a JSX list:
 *
 * Monday, Tuesday, Wednesday, Thursday, Friday : 9:00AM to 7:00PM
 * Saturday : 12:00AM to 5:00PM
 *
 */
function displayBusinessHoursAsListGroupedByHours(businessHours: BusinessHourDays): ReactNode {
  const groupByIdenticalHours: Record<string, Array<CalendarWeekDaysShortEnum>> = {};

  Object.entries<BusinessHour | null>(businessHours).forEach(([day, hours]) => {
    const isCalendarWeekDaysShortEnum = makeEnumTypeGuard(CalendarWeekDaysShortEnum);

    if (!hours || !isCalendarWeekDaysShortEnum(day)) {
      return;
    }
    const stringifiedHours = `${hours.opens}-${hours.closes}`;
    if (!groupByIdenticalHours[stringifiedHours]) {
      groupByIdenticalHours[stringifiedHours] = [day];
    } else {
      groupByIdenticalHours[stringifiedHours].push(day);
    }
  });

  return (
    <StyledUl>
      {Object.entries(groupByIdenticalHours).map(([hour, days]) => {
        const [opens, closes] = hour.split('-');
        return (
          // This renders a row: "Monday, Friday : 9:00AM to 7:00PM"
          <StyledLi>
            {days.map((day, index) => (
              <>
                {mapShortToLongWeekDay[day]}
                {index + 1 < days.length ? ', ' : ''}
              </>
            ))}{' '}
            :{' '}
            <Trans id="business-hours-opens-to-close">
              {opens} to {closes}
            </Trans>
          </StyledLi>
        );
      })}
    </StyledUl>
  );
}
